import * as React from 'react'

import { Box, Section, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { Panes } from 'modules/core/components/Panes'
import PanesHeader from 'modules/core/components/PanesHeader'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'

import { usePerformanceModuleContext } from 'modules/performance/hooks/usePerfomanceModuleContext'
import CompetencyAssessmentToggle from 'modules/reviews/components/Competencies/CompetencyAssessmentToggle'
import { ReviewTypeItem } from 'modules/reviews/components/Questions/ReviewTypeItem'
import ReviewShowWrapper from 'modules/reviews/components/Show/ReviewShowWrapper'
import { useQuestionConfigs } from 'modules/reviews/hooks/useQuestionConfigs'
import { useReviewees } from 'modules/reviews/hooks/useReviewees'
import { getGroupName } from 'modules/reviews/lib/getGroupName'
import groups from 'modules/reviews/store/collections/groups'
import questionCategories from 'modules/reviews/store/collections/questionCategories'
import questionGroupConfigsColl from 'modules/reviews/store/collections/questionGroupConfigs'
import type Review from 'modules/reviews/store/models/v2/Review'

import { useReviewShowContext } from '../hooks'
import PerformanceScoreToggle from './PerformanceScoreToggle'

function useQuestionGroups(form: Review) {
  const formId = form.attributes.id
  const [questionGroupConfigs, setQuestionGroups] = React.useState(
    questionGroupConfigsColl.filter({
      surveys_form_id: formId,
    })
  )

  const { loading } = useAsyncData(async () => {
    await Promise.all([
      questionGroupConfigsColl.fetch({
        data: {
          surveys_form_id: formId,
        },
      }),
      questionCategories.fetch({
        data: {
          surveys_form_id: formId,
        },
      }),
      groups.fetch(),
    ])
    setQuestionGroups(
      questionGroupConfigsColl.filter({
        surveys_form_id: formId,
      })
    )
  }, [])

  return {
    loading,
    questionGroupConfigs,
  }
}

function QuestionsPage() {
  const { review } = useReviewShowContext()
  const { loading, questionGroupConfigs } = useQuestionGroups(review)
  const refs = questionGroupConfigs.map(() => React.createRef<HTMLElement>())

  const { performanceFeatureAvailability } = usePerformanceModuleContext()
  const canSeeCompetencies = performanceFeatureAvailability.canSeeCompetencies

  const onClick = (i: number) => {
    window.scrollTo(0, refs[i]?.current?.offsetTop ?? 0)
  }

  const { loading: loadingReviewees, reviewees } = useReviewees(review)
  const { loading: loadingQuestions } = useQuestionConfigs(review.id)

  const revieweesIds = new Set(Object.keys(reviewees))

  if (loading || loadingReviewees || loadingQuestions) return <Loading />

  const showQuestionsSettings = canSeeCompetencies || review.showScoreToggle

  return (
    <ReviewShowWrapper reviewId={Number(review.attributes.id)}>
      <Box gap="s24">
        {showQuestionsSettings && (
          <>
            <Section
              illustration="settings"
              title={i18n.t('surveys.review.competencies.general_options')}
              description={i18n.t('surveys.review.competencies.general_description')}
            >
              <Box gap="s32">
                {review.showScoreToggle && <PerformanceScoreToggle review={review} />}
                <CompetencyAssessmentToggle review={review} revieweesIds={revieweesIds} />
              </Box>
            </Section>
            <Box
              border={{
                bottom: {
                  width: 's1',
                  style: 'solid',
                  color: 'grey400',
                },
              }}
            />
          </>
        )}
        <Box paddingX="s64" paddingY="s32">
          <Panes direction="horizontal" type="naked">
            <Box>
              <PanesHeader
                illustration="tasks_management"
                separation
                title={i18n.t('surveys.review.questions.title')}
                description={i18n.t('surveys.review.questions.description')}
              />
              <Box aria-label="Questions section" gap="s8">
                {questionGroupConfigs.map((questionGroupConfig, i) => (
                  <Box
                    key={questionGroupConfig.attributes.id}
                    padding="s8"
                    borderRadius={{
                      all: 'abs008',
                    }}
                    cursor="pointer"
                    onClick={() => onClick(i)}
                  >
                    <Text color="grey800" size="300" weight="medium">
                      {getGroupName(questionGroupConfig.respondent)}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box>
              {!loading && (
                <>
                  {questionGroupConfigs.map((questionGroupConfig, i) => (
                    <Box key={questionGroupConfig.attributes.id} ref={refs[i]} paddingBottom="s48">
                      <ReviewTypeItem
                        questionGroupConfig={questionGroupConfig}
                        form={review}
                        hasPerformanceScoreQuestions={review.hasScoreQuestions}
                      />
                    </Box>
                  ))}
                </>
              )}
            </Box>
          </Panes>
        </Box>
      </Box>
    </ReviewShowWrapper>
  )
}
export default observer(QuestionsPage)
